/**
 * GanttDashboardSection - Sezione dashboard con Gantt compatto per ogni commessa attiva.
 *
 * Per ogni commessa mostra:
 * - Header: numero (rosso se in ritardo), titolo, cliente, consegna
 * - Mini-Gantt: barre orizzontali per ogni fase, scalate sulla durata totale
 *   - Verde = completata, Blu = in progress, Rosso = in ritardo, Grigio = non iniziata
 *   - Clic su barra con documenti → popup lista allegati
 * - Clic su header → navigazione alla commessa
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Paperclip, Download, X, ExternalLink, AlertTriangle, BarChart2 } from 'lucide-react'
import api from '../../core/services/api'

interface FaseGantt {
  id: string
  titolo: string
  tipo: string
  durata_prevista: number
  completata: boolean
  in_ritardo: boolean
  giorni_effettivi: number
  offset_inizio: number
  offset_fine: number
  data_inizio_prevista: string | null
  data_fine_prevista: string | null
  ha_allegati: boolean
  num_allegati: number
}

interface CommessaGantt {
  id: string
  numero_commessa: string
  titolo: string
  cliente_nome: string
  stato: string
  data_inizio: string | null
  data_prevista_consegna: string | null
  in_ritardo: boolean
  fasi: FaseGantt[]
}

interface AllegatoItem {
  id: string
  titolo: string
  filename: string
  content_type: string
  file_size: number
  created_at: string
  versione: number
}

interface FasePopup {
  faseId: string
  faseTitolo: string
  x: number
  y: number
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b}B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)}KB`
  return `${(b / (1024 * 1024)).toFixed(1)}MB`
}

function barColor(fase: FaseGantt): string {
  if (fase.completata) return 'bg-green-500'
  if (fase.in_ritardo) return 'bg-red-500'
  if (fase.giorni_effettivi > 0) return 'bg-blue-500'
  return 'bg-gray-300'
}

function barOpacity(fase: FaseGantt): string {
  return fase.completata ? 'opacity-70' : 'opacity-100'
}

export default function GanttDashboardSection() {
  const navigate = useNavigate()
  const [commesse, setCommesse] = useState<CommessaGantt[]>([])
  const [loading, setLoading] = useState(true)
  const [popup, setPopup] = useState<FasePopup | null>(null)
  const [popupAllegati, setPopupAllegati] = useState<AllegatoItem[]>([])
  const [popupLoading, setPopupLoading] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api.get('/v1/commesse/gantt-overview')
      .then(res => setCommesse(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Chiudi popup clic fuori
  useEffect(() => {
    if (!popup) return
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopup(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [popup])

  const handleFaseClick = async (fase: FaseGantt, e: React.MouseEvent) => {
    if (!fase.ha_allegati) return
    e.stopPropagation()
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setPopup({ faseId: fase.id, faseTitolo: fase.titolo, x: rect.left, y: rect.bottom + 8 })
    setPopupAllegati([])
    setPopupLoading(true)
    try {
      const res = await api.get(`/v1/allegati/fase_commessa/${fase.id}`)
      // Mostra solo versione più recente per gruppo
      const seen = new Set<string>()
      const latest: AllegatoItem[] = []
      for (const a of res.data) {
        const key = a.gruppo_id || a.id
        if (!seen.has(key)) { seen.add(key); latest.push(a) }
      }
      setPopupAllegati(latest)
    } catch { setPopupAllegati([]) }
    finally { setPopupLoading(false) }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    )
  }

  if (commesse.length === 0) return null

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <BarChart2 className="w-5 h-5 text-primary-600" />
        Gantt Commesse
      </h2>

      <div className="space-y-4">
        {commesse.map(commessa => {
          const totalSpan = commessa.fasi.length > 0
            ? Math.max(...commessa.fasi.map(f => f.offset_fine))
            : 1

          return (
            <div key={commessa.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              {/* Header commessa */}
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100"
                onClick={() => navigate(`/app/commesse/${commessa.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`font-mono text-sm font-bold ${commessa.in_ritardo ? 'text-red-600' : 'text-primary-700'}`}>
                    {commessa.numero_commessa}
                    {commessa.in_ritardo && (
                      <AlertTriangle className="w-3.5 h-3.5 inline ml-1 text-red-500" />
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-gray-900 truncate">{commessa.titolo}</p>
                    <p className="text-xs text-gray-400 truncate">{commessa.cliente_nome}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                  {commessa.data_prevista_consegna && (
                    <span className={`text-xs ${commessa.in_ritardo ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      Cons. {formatDate(commessa.data_prevista_consegna)}
                    </span>
                  )}
                  <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                </div>
              </div>

              {/* Mini-Gantt */}
              {commessa.fasi.length > 0 ? (
                <div className="px-4 py-3 space-y-1.5">
                  {commessa.fasi.map(fase => {
                    const leftPct = (fase.offset_inizio / totalSpan) * 100
                    const widthPct = Math.max(1, ((fase.offset_fine - fase.offset_inizio) / totalSpan) * 100)

                    return (
                      <div key={fase.id} className="flex items-center gap-2">
                        {/* Label fase */}
                        <div className="w-36 flex-shrink-0">
                          <p className={`text-xs truncate ${fase.completata ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                            {fase.titolo}
                          </p>
                        </div>

                        {/* Barra */}
                        <div className="flex-1 relative h-5">
                          <div className="absolute inset-0 bg-gray-100 rounded" />
                          <div
                            className={`absolute top-0 h-full rounded cursor-pointer transition-opacity
                              ${barColor(fase)} ${barOpacity(fase)}
                              ${fase.ha_allegati ? 'ring-1 ring-offset-0 ring-amber-400' : ''}
                            `}
                            style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                            title={`${fase.titolo} · ${fase.giorni_effettivi}/${fase.durata_prevista}gg${fase.ha_allegati ? ` · ${fase.num_allegati} doc` : ''}`}
                            onClick={(e) => handleFaseClick(fase, e)}
                          >
                            {/* Indicatore documenti */}
                            {fase.ha_allegati && (
                              <div className="absolute right-0.5 top-0.5 w-3.5 h-3.5 bg-amber-400 rounded-sm flex items-center justify-center">
                                <Paperclip className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                            {/* Progress interno */}
                            {!fase.completata && fase.giorni_effettivi > 0 && (
                              <div
                                className="absolute top-0 left-0 h-full bg-white/20 rounded-l"
                                style={{ width: `${Math.min(100, (fase.giorni_effettivi / fase.durata_prevista) * 100)}%` }}
                              />
                            )}
                          </div>
                        </div>

                        {/* Durata */}
                        <div className="w-12 text-right flex-shrink-0">
                          <span className={`text-xs ${fase.in_ritardo ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                            {fase.giorni_effettivi}/{fase.durata_prevista}g
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="px-4 py-3 text-xs text-gray-400">Nessuna fase definita</div>
              )}
            </div>
          )
        })}
      </div>

      {/* Popup allegati fase */}
      {popup && (
        <div
          ref={popupRef}
          className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 w-80"
          style={{ left: Math.min(popup.x, window.innerWidth - 320), top: popup.y }}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <div className="flex items-center gap-1.5">
              <Paperclip className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-gray-800 truncate max-w-[200px]">{popup.faseTitolo}</span>
            </div>
            <button onClick={() => setPopup(null)} className="p-0.5 hover:bg-gray-100 rounded">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <div className="p-2 max-h-60 overflow-y-auto">
            {popupLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              </div>
            ) : popupAllegati.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-3">Nessun documento</p>
            ) : (
              popupAllegati.map(a => (
                <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded-lg group">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{a.titolo}</p>
                    <p className="text-xs text-gray-400 truncate">{a.filename} · {formatBytes(a.file_size)}</p>
                  </div>
                  <a
                    href={`/api/v1/allegati/download/${a.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1 text-gray-400 hover:text-primary-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Scarica"
                    onClick={e => e.stopPropagation()}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
